import React from "react";
import { displayResultMessage, openMessage } from "utils/misc";
import Modal from "common/Modal/Modal";
import Text from "common/Typography/Text";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces, RequestSource } from "@shared/types";
import { useUser } from "@shared/utils/hooks/misc.ts";
import { sharedLib } from "@shared/services/shared.service.ts";
import { useTheme } from "styled-components";

interface DeleteAccountConfirmModalProps {
  visible: boolean;
  closeModal: () => void;
  onSuccess: () => void;
}

const DeleteAccountConfirmModal = ({
  visible,
  closeModal,
  onSuccess,
}: DeleteAccountConfirmModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SETTINGS);
  const { user } = useUser();
  const { isNewLook } = useTheme();
  const [deleteCornixAccountConfirmationEmail, { isLoading }] =
    sharedLib.sharedApi.authentication.useDeleteCornixAccountConfirmationEmailMutation();

  const onDeleteAccount = async () => {
    if (user?.email) {
      displayResultMessage(
        deleteCornixAccountConfirmationEmail({
          email: user.email,
          request_source: RequestSource.APP,
        }).unwrap(),
        "Account deletion email sent successfully",
        "Failed to send account deletion email"
      )
        .then(() => {
          closeModal();
          onSuccess();
        })
        .catch(() => {});
    } else {
      openMessage(t("delete_account_modal.error_restart"), "error");
    }
  };

  return (
    <Modal
      open={visible}
      title={t("delete_account_modal.title")}
      cancelText={t("delete_account_modal.no")}
      onCancel={closeModal}
      okText={t("delete_account_modal.yes")}
      onOk={onDeleteAccount}
      okButtonProps={{
        loading: isLoading,
        // New look: the irreversible action is the one red control.
        ...(isNewLook && { danger: true }),
      }}
    >
      <div style={{ marginBottom: 8 }}>
        <Text>{t("delete_account_modal.content")}</Text>
      </div>
      <div>
        <Text>{t("delete_account_modal.confirm_message")}</Text>
      </div>
    </Modal>
  );
};

export default DeleteAccountConfirmModal;
